import { useEffect, useState } from "react";
import { Lock, Shield, User } from "lucide-react";
import PasswordInput from "../../components/PasswordInput";
import SuperadminPanel from "./superadmin-panel";
import ClientServiceP from "../../services/client/ClientPService";
import { getSuperadminSession, saveSuperadminSession, clearSuperadminSession } from "../../services/superadmin/superadminSession";
import { SpinnerCarga } from "../../components/SpinnerCarga";
import turnlinkLogo from "../../assets/turnlinkLOGO.png";
import "./superadmin.css";

const THEME_COLOR = "#EFF6F1"; // = --sa-bg (salvia de marca); la barra del navegador en el cel toma este color

/**
 * /admin — panel de superadmin con su propia ruta. La sesión vive en el JWT guardado en
 * localStorage y se valida al montar, así que recargar NO desloguea (antes el panel vivía
 * dentro de la landing en `/`, que borraba la sesión a propósito en cada carga).
 */
export default function AdminPage() {
  const [session, setSession] = useState(() => getSuperadminSession());
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Igual que las demás páginas: quitar el loader inicial y el footer estático (SEO)
  // que trae index.html; si no, tapan el panel. Y fijar el theme-color del panel
  // (salvia) mientras esta página esté montada.
  useEffect(() => {
    document.getElementById("initial-loader")?.remove();
    document.body.classList.add("js-loaded");
    let meta = document.querySelector('meta[name="theme-color"]');
    const created = !meta;
    if (!meta) { meta = document.createElement("meta"); meta.name = "theme-color"; document.head.appendChild(meta); }
    const prev = meta.content;
    meta.content = THEME_COLOR;

    // Manifest propio, solo mientras /admin está montada. index.html no enlaza
    // ningún manifest a propósito: así los negocios instalan su PWA desde la URL en
    // la que están y les abre ahí. Aquí sí hace falta uno para que la instalación
    // arranque en /admin con nombre, ícono y splash propios.
    const existing = document.querySelector('link[rel="manifest"]');
    const link = existing || Object.assign(document.createElement("link"), { rel: "manifest" });
    const prevHref = existing?.getAttribute("href");
    link.setAttribute("href", "/admin.manifest.json");
    if (!existing) document.head.appendChild(link);
    // iPhone no lee el manifest: el ícono de inicio sale del apple-touch-icon del head.
    const touchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    const prevTouchIcon = touchIcon?.getAttribute("href");
    touchIcon?.setAttribute("href", "/admin-icon-180.png");
    const iosMetas = [
      ["apple-mobile-web-app-capable", "yes"],
      ["apple-mobile-web-app-title", "TL Admin"],
      // "default": barra de estado opaca con texto oscuro; con fondo claro,
      // "black-translucent" dejaba la hora en blanco sobre salvia.
      ["apple-mobile-web-app-status-bar-style", "default"],
    ].map(([name, content]) => {
      const m = document.createElement("meta");
      m.name = name; m.content = content;
      document.head.appendChild(m);
      return m;
    });

    return () => {
      if (created) meta.remove(); else meta.content = prev;
      if (existing) link.setAttribute("href", prevHref || ""); else link.remove();
      if (touchIcon && prevTouchIcon) touchIcon.setAttribute("href", prevTouchIcon);
      iosMetas.forEach((m) => m.remove());
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await ClientServiceP.loginSuperadmin(usuario, password);
      saveSuperadminSession(res.access_token);
      setSession(getSuperadminSession());
      setPassword("");
    } catch (err) {
      setError(err?.response?.status === 401 ? "Usuario o contraseña incorrectos" : "No se pudo iniciar sesión, intenta de nuevo");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearSuperadminSession();
    setSession(null);
  };

  if (session) {
    return (
      <>
        <SuperadminPanel onLogout={handleLogout} />
        <SpinnerCarga />
      </>
    );
  }

  return (
    <div className="sa-root sa-login">
      <form onSubmit={handleLogin} className="sa-login-card">
        <div className="sa-login-brand">
          <span className="tl-wordmark sa-login-logo"><img src={turnlinkLogo} alt="" /><span>turn<em>link</em></span></span>
          <h1><Shield size={16} style={{ color: "var(--sa-brand)" }} /> Superadmin</h1>
          <p className="sa-muted">Solo para administradores autorizados</p>
        </div>

        <label className="sa-field">
          <span><User size={13} /> Usuario</span>
          <input value={usuario} onChange={(e) => setUsuario(e.target.value)} required autoFocus disabled={loading} autoComplete="username" autoCapitalize="none" autoCorrect="off" enterKeyHint="next" />
        </label>
        <label className="sa-field">
          <span><Lock size={13} /> Contraseña</span>
          <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required disabled={loading} autoComplete="current-password" enterKeyHint="go" />
        </label>

        {error && <div className="sa-error" role="alert">{error}</div>}

        <button type="submit" className="sa-btn sa-btn-primary sa-btn-block" disabled={loading}>
          {loading ? "Iniciando sesión…" : "Iniciar sesión"}
        </button>
      </form>
    </div>
  );
}
